import { type FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
// MUI
import { Box, Typography } from '@mui/material';

// common
import OperationCodeField from 'common/components/OperationCodeField';
import SchemaForm from 'common/SchemaForm';
import { formatDateWithoutTimezone } from 'common/utils';

// screen
import DialogWrapper from 'modules/suppliers/detail/components/modals/DialogWrapper';
import PdfViewer from 'modules/suppliers/detail/components/modals/PdfViewer';
import useGetBusinessRegistrationCertificateDetails, {
  BusinessRegistrationCertificateDetail,
} from 'modules/suppliers/services/useGetBusinessRegistractionCertificateDetails';
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import { BusinessRegistrationCertificate } from 'modules/suppliers/services/useBusinessRegistrationCertificate';
import PdfList from 'modules/suppliers/detail/components/modals/details/PdfList';

interface BusinessRegistrationDetailsProps {
  open: boolean;
  onClose: () => void;
  item: BusinessRegistrationCertificate;
}

interface CustomFormData extends BusinessRegistrationCertificateDetail {
  customSwitch: boolean;
}

const BusinessRegistrationDetails: FC<BusinessRegistrationDetailsProps> = (props) => {
  const { open, onClose, item } = props;

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [selectedPdfFileName, setSelectedPdfFileName] = useState<string>('');

  const { formatMessage } = useIntl();

  const { params } = useTabsData();

  const { data } = useGetBusinessRegistrationCertificateDetails({
    supplierProfileOperationCode: params.supplierOperationCode,
    customerOperationCode: params.customerOperationCode,
    businessRegistrationOperationCode: item.operationCode,
  });

  if (!data) return null;

  return (
    <DialogWrapper
      title={
        <>
          <FormattedMessage id="supplier.businessRegistration.detailsModalTitle" />:{' '}
          {data.registrationNumber}
        </>
      }
      open={open}
      onClose={onClose}
    >
      <SchemaForm<CustomFormData>
        formHookProps={{
          values: {
            ...data,
            name: data.name || '',
            type: data.type || '',
            publishedAt: data.publishedAt ? formatDateWithoutTimezone(data.publishedAt) : '',
            firstIssuedAt: data.firstIssuedAt ? formatDateWithoutTimezone(data.firstIssuedAt) : '',
            expiredAt: data.isUnlimited
              ? formatMessage({ id: 'noExpiration' })
              : data.expiredAt
                ? formatDateWithoutTimezone(data.expiredAt)
                : '',
            customSwitch: data.expirationDate ? false : true,
          } as CustomFormData,
        }}
        globalProps={{ readOnly: true }}
        schema={{
          fields: ({
            formProps: {
              intl: { formatMessage },
            },
          }) => {
            return {
              registrationNumber: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.number',
                }),
                placeholder: formatMessage({
                  id: 'supplier.businessRegistration.number',
                }),
                rules: { required: true },
              },
              operationCode: {
                mode: 'raw',
                propsWrapper: { desktop: 6 },
                render: (params) => (
                  <OperationCodeField
                    {...params}
                    label={formatMessage({
                      id: 'supplier.businessRegistration.operationCode',
                    })}
                  />
                ),
              },
              name: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.name',
                }),
                placeholder: formatMessage({
                  id: 'supplier.businessRegistration.name',
                }),
                rules: { required: true },
              },
              taxCode: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.taxNumber',
                }),
                placeholder: formatMessage({
                  id: 'supplier.businessRegistration.taxNumber',
                }),
              },
              type: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.classification',
                }),
                placeholder: formatMessage({
                  id: 'supplier.businessRegistration.classification',
                }),
                rules: { required: true },
              },
              issuedBy: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.issuePlace',
                }),
                placeholder: formatMessage({
                  id: 'supplier.businessRegistration.issuePlace',
                }),
              },
              representative: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.generalDetails.representative',
                }),
                placeholder: formatMessage({
                  id: 'supplier.generalDetails.representative',
                }),
              },
              cityName: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.generalDetails.stateOrCity',
                }),
                placeholder: formatMessage({
                  id: 'supplier.generalDetails.stateOrCity',
                }),
                rules: { required: true },
              },
              publishedAt: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.issueDate',
                }),
                placeholder: formatMessage({
                  id: 'supplier.businessRegistration.issueDate',
                }),
                rules: { required: true },
              },
              districtName: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.generalDetails.formerDistrictName',
                }),
                placeholder: formatMessage({
                  id: 'supplier.generalDetails.formerDistrictName',
                }),
              },
              firstIssuedAt: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.firstIssueDate',
                }),
                placeholder: formatMessage({
                  id: 'supplier.businessRegistration.firstIssueDate',
                }),
              },
              wardName: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.generalDetails.ward',
                }),
                placeholder: formatMessage({
                  id: 'supplier.generalDetails.ward',
                }),
                rules: { required: true },
              },
              expiredAt: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.expirationDate',
                }),
                placeholder: formatMessage({
                  id: 'supplier.businessRegistration.expirationDate',
                }),
                rules: { required: true },
              },
              addressDetail: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.generalDetails.address',
                }),
                placeholder: formatMessage({
                  id: 'supplier.generalDetails.address',
                }),
                rules: { required: true },
              },
              isUnlimited: {
                mode: 'switch',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.businessRegistration.noExpiration',
                }),
                labelPlacement: 'top',
              },
              hasPdf: {
                mode: 'raw',
                render: () => {
                  const pdfPaths = (data.pdfPath as string[]) || [];

                  return !pdfPaths.length ? null : (
                    <Box>
                      <Typography sx={{ marginBottom: 1, fontWeight: 500 }}>
                        PDF Documents:
                      </Typography>
                      <Typography>
                        <PdfList
                          pdfPaths={pdfPaths}
                          onPdfClick={(pdfUrl, fileName) => {
                            setSelectedPdf(pdfUrl);
                            setSelectedPdfFileName(fileName);
                            setPdfModalOpen(true);
                          }}
                        />
                      </Typography>
                    </Box>
                  );
                },
              },
            };
          },
        }}
      />
      <PdfViewer
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfUrl={selectedPdf}
        title={selectedPdfFileName || 'PDF Document'}
      />
    </DialogWrapper>
  );
};

export default BusinessRegistrationDetails;
